// import { createContext } from 'react' // Not used in the provided snippet
// import FirebaseApp from 'firebase/app' // Using compat version below
// import 'firebase/firestore' // Using compat version below
import FirebaseApp from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/messaging'; // For FCM

// Configuration for Firebase
const config = {
  appId: process.env.REACT_APP_APP_ID,
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
};

// Helper function to set up Firebase Cloud Messaging
const setupFCM = (app, vapidKey) => {
  console.log('Setting up FCM...');
  const messaging = app.messaging();

  // Get FCM token
  messaging.getToken({ vapidKey })
    .then(currentToken => {
      if (currentToken) {
        console.log('Current FCM token:', currentToken);
        // TODO: Send this token to your server to associate it with the user if needed
      } else {
        // Show permission request UI if library is used in a browser
        // that supports force-refreshing the token.
        console.log('Failed to get FCM token: No token received. This can happen if permission is not granted or if running in an environment without FCM support.');
      }
    })
    .catch(err => {
      console.error('Error retrieving FCM token:', err);
    });

  // Handle incoming messages when the app is in the foreground
  messaging.onMessage((payload) => {
    try {
      console.log('FCM message received in foreground:', payload);

      // Adjust payload access as needed. FCM messages can have notification and/or data payloads.
      const notificationPayload = payload.notification || payload.data;
      const title = notificationPayload?.title || 'Notification';
      const body = notificationPayload?.body || '';
      // Example: Check for severity in payload.data, default to 'info'
      const severity = payload.data?.severity || 'info';
      // const link = payload.data?.link; // If you plan to support links

      const detail = {
        title,
        body,
        severity,
        // link,
      };
      // Dispatch a custom event to show an in-app notification
      const event = new CustomEvent('show-in-app-notification', { detail });
      window.dispatchEvent(event);
    } catch (err) {
      console.error('Error in messaging.onMessage handler:', err);
    }
  });

  // Handle token refresh
  // Corrected: Assign the function to the onTokenRefresh property
  messaging.onTokenRefresh = async () => {
    console.log('FCM token refresh event occurred.');
    try {
      // Re-fetch token with VAPID key
      const newToken = await messaging.getToken({ vapidKey });
      if (newToken) {
        console.log('New FCM token:', newToken);
        const user = FirebaseApp.auth().currentUser;
        if (user && user.uid) {
          try {
            // Dynamically import your function to update the token in Firestore
            // Ensure the path './obj/notificationTopic' is correct relative to this file
            const { updateUserToken } = await import('./obj/notificationTopic');
            await updateUserToken(user.uid, newToken);
            console.log('User FCM token updated successfully in Firestore for user:', user.uid);
          } catch (error) {
            console.error('Failed to update user FCM token in Firestore:', error);
          }
        } else {
          console.log('No authenticated user found. New token not updated in Firestore.');
        }
      } else {
        console.log('Failed to get new FCM token during refresh.');
      }
    } catch (error) {
      console.error('Error during FCM token refresh process:', error);
    }
  };
};

// Initialize Firebase Cloud Messaging
const initializeMessaging = (app) => {
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

  if (!vapidKey) {
    console.warn("REACT_APP_FIREBASE_VAPID_KEY is missing from .env file. FCM getToken might fail or use a default key if configured in Firebase console.");
  }

  // Check for browser support for Notifications, Service Worker, and Push API
  if (("Notification" in window) && ("serviceWorker" in navigator) && ("PushManager" in window)) {
    if (Notification.permission === "granted") {
      console.log('Notification permission already granted.');
      setupFCM(app, vapidKey);
    } else if (Notification.permission === 'default') {
      console.log('Notification permission is default. Requesting permission...');
      // Request permission from the user
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          console.log('Notification permission granted after request.');
          setupFCM(app, vapidKey);
        } else {
          console.log('Notification permission denied after request.');
        }
      }).catch(err => {
        console.error('Error requesting notification permission:', err);
      });
    } else {
      console.log('Notification permission is denied.');
    }
  } else {
    console.log('Notifications, Service Worker, or Push API not supported in this browser. FCM will not be initialized.');
  }
};

class Firebase {
  constructor() {
    let appInstance;
    if (!FirebaseApp.apps.length) {
      appInstance = FirebaseApp.initializeApp(config);
      console.log('Firebase app initialized.');

      // Initialize Firebase Cloud Messaging
      initializeMessaging(appInstance);

      // Firestore persistence (user had this commented out)
      /*
      const firestore = appInstance.firestore();
      firestore.enablePersistence({ synchronizeTabs: true })
        .then(() => console.log("Firestore persistence enabled with tab synchronization."))
        .catch(err => {
            if (err.code === 'failed-precondition') {
                console.warn("Firestore persistence failed: Usually means multiple tabs are open or another process is using persistence. Offline support might be limited to this tab.");
            } else if (err.code === 'unimplemented') {
                console.warn("Firestore persistence failed: The browser does not support all of the features required to enable persistence.");
            } else {
                console.error("Firestore persistence error:", err);
            }
        });
      this.db = firestore;
      */
    } else {
      appInstance = FirebaseApp.app(); // Get the default app if already initialized
      console.log('Firebase app already initialized. Using existing instance.');
      // If messaging needs to be re-initialized or checked on subsequent instantiations,
      // you might call initializeMessaging(appInstance) here as well,
      // but typically FCM setup is a one-time operation per page load.
    }

    // If persistence was not set up above, initialize this.db here
    if (!this.db) {
      this.db = FirebaseApp.firestore();
    }

    // Firestore collection references
    this.businessInfoCollection = this.db.collection('businessInfo');
    this.clientsCollection = this.db.collection('clients');
    this.configCollection = this.db.collection('config');
    this.errorsCollection = this.db.collection('errors');
    this.orderStateCollection = this.db.collection('orderState');
    this.ordersCollection = this.db.collection('orders');
    this.productsCollection = this.db.collection('products');
    this.staffCollection = this.db.collection('staff');
    this.usersCollection = this.db.collection('users');
  }

  // Add other Firebase methods as needed
}

// const FirebaseContext = createContext(null); // Not used in the provided snippet
/*
  export { Firebase, FirebaseContext, FirebaseApp } // FirebaseApp is already available via import
*/

export default Firebase;
